import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { PlaybookProposalOutcomeAggregates } from "@repo/app-types/entities/playbookProposalDetail";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useState } from "react";
import { useLazyFetchSampleToolResultGradingsQuery } from "../../api/entities/playbook.js";
import type { SampleToolResultOutcomeEntry } from "../../hooks/useSampleToolResults.js";
import { GradedCallsSlideover } from "./gradedCallsSlideover.js";
import { OutcomeDistributionComparisonChart } from "./outcomeDistributionComparisonChart.js";
import { OutcomeHealthCheckCard } from "./outcomeHealthCheckCard.js";

interface OutcomeSuggestionResultsProps {
  /**
   * Undefined when no saved sample-tool result exists for the outcome yet —
   * the pane then renders the empty placeholder so the column stays present.
   */
  aggregates: PlaybookProposalOutcomeAggregates | undefined;
  /** The full sample tool result entry, used to fetch grading details. */
  suggestion?: SampleToolResultOutcomeEntry;
  /** Drives the distribution chart and graded-calls labels. */
  outcomeDefinition: OutcomeDefinition;
  playbookId?: PlaybookId | null;
}

export const OutcomeSuggestionResults: FC<OutcomeSuggestionResultsProps> = ({
  aggregates,
  suggestion,
  outcomeDefinition,
  playbookId,
}) => {
  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);
  const [fetchGradings, { data: gradingsData, isFetching, isError }] =
    useLazyFetchSampleToolResultGradingsQuery();

  const openGradedCalls = () => {
    if (playbookId && suggestion) {
      void fetchGradings({
        playbookId,
        sampleToolResultId: suggestion.id,
      });
    }
    setIsSlideoverOpen(true);
  };

  if (!aggregates) {
    return (
      <div className="flex min-h-[160px] flex-1 items-center justify-center text-center">
        <p className="max-w-[220px] text-xs text-muted-foreground">
          The test results of your outcome will appear here.
        </p>
      </div>
    );
  }

  const hasPlaybookContext =
    suggestion !== undefined && playbookId !== undefined && playbookId !== null;

  const hasSampleGrades = aggregates.sample_size > 0;

  const showGradedCallsButton = hasPlaybookContext && hasSampleGrades;

  return (
    <>
      <div className="space-y-4">
        {suggestion?.health_check ? (
          <OutcomeHealthCheckCard healthCheck={suggestion.health_check} />
        ) : null}

        <div className="rounded-md bg-white px-4 pt-3 pb-4 shadow-sm ring-1 ring-black/5">
          <OutcomeDistributionComparisonChart
            baseline={aggregates.baseline}
            tested={aggregates.tested}
            outcomeDefinition={outcomeDefinition}
          />
        </div>

        {showGradedCallsButton ? (
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            onClick={openGradedCalls}
            className="w-full justify-center"
          >
            See graded calls
          </Button>
        ) : null}
      </div>

      <GradedCallsSlideover
        nodeType="outcome"
        initialTab="sample"
        isOpen={isSlideoverOpen}
        onClose={() => setIsSlideoverOpen(false)}
        gradings={
          gradingsData?.node_type === "outcome" ? gradingsData : undefined
        }
        outcomeDefinition={outcomeDefinition}
        isLoading={isFetching}
        isError={isError}
      />
    </>
  );
};
